// Wallet — small module for the real server balance and a tiny event
// bus so the NavBar chip + Wallet page + every game lobby all stay in
// sync without prop-drilling a context through the tree.
//
// Backend source of truth: GET /api/me/wallet (auth required). The
// wallet is per-user, not per-game, so it lives under /me, not under
// any specific game's route.
// Units: "points" == INR rupees (the backend column is balance_points
// and PHP treats 1 point =1). No paise conversion anywhere.

import { useEffect, useState } from 'react';
import { api } from './api';

type WalletState = { balance: number; currency: string; loading: boolean; error?: string };

type Listener = (s: WalletState) => void;
const listeners = new Set<Listener>();
let state: WalletState = { balance: 0, currency: 'INR', loading: true };

function emit() { for (const l of listeners) l(state); }

export async function refreshWallet(): Promise<void> {
  state = { ...state, loading: true, error: undefined };
  emit();
  try {
    const data = await api.get<{ balance: number; currency: string }>('/me/wallet');
    state = { balance: data.balance | 0, currency: data.currency || 'INR', loading: false };
  } catch (e) {
    state = { ...state, loading: false, error: (e as Error).message };
  }
  emit();
}

// Called from a game WS when the server pushes a wallet_update — keeps
// the chip/page in sync after a bet debit or a win credit without a
// round-trip to /me/wallet.
export function setWalletBalance(next: number) {
  state = { ...state, balance: next | 0, loading: false };
  emit();
}

export function getWallet(): WalletState { return state; }

export function useWallet(): WalletState {
  const [s, setS] = useState<WalletState>(state);
  useEffect(() => {
    const l: Listener = (n) => setS(n);
    listeners.add(l);
    // Kick a refresh on first mount if we've never loaded.
    if (state.loading) void refreshWallet();
    return () => { listeners.delete(l); };
  }, []);
  return s;
}
